/**
 * Call overlay (batch 6b) — Signal-style incoming/active call UI.
 *
 * Single top-level div mounted once per app lifetime. It renders different
 * content based on the call-session phase:
 *
 *   - `ringing-in`: full-screen overlay, phone number + Accept/Decline.
 *     Can't be accidentally dismissed — Escape declines, Enter accepts, click
 *     outside does nothing.
 *   - `ringing-out`: compact active-call card in "calling…" state.
 *   - `active`:     active-call card with mute/speaker/keypad/minimise/end.
 *                   Minimised = pill at bottom-right showing the live timer.
 *
 * The overlay subscribes to `onCallChange` and re-renders on every state
 * change. It renders nothing when no call is active.
 *
 * Presentation is entirely in CSS (tokens.css + components.css).
 * Zero inline styles — no hardcoded hex values in this file.
 */

import {
  onCallChange,
  acceptIncoming,
  decline,
  hangup,
  toggleMute,
  toggleSpeaker,
  toggleMinimise,
  sendDtmf,
  ensureCallSubscription,
  type CallUiState,
} from './call-session';
import { state } from '../state';

const OVERLAY_ID = 'call-overlay-root';
let mounted = false;
let timerInterval: ReturnType<typeof setInterval> | null = null;
let lastPhase: CallUiState['phase'] | 'idle' = 'idle';
let keypadOpen = false;

/** Mount the overlay once on app start. Idempotent. */
export function mountCallOverlay(): void {
  if (mounted) return;
  mounted = true;
  let root = document.getElementById(OVERLAY_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = OVERLAY_ID;
    document.body.appendChild(root);
  }
  onCallChange(renderOverlay);
  ensureCallSubscription();
  // Re-subscribe when the user switches servers so each server's #calls
  // ring bubbles up here.
  setInterval(ensureCallSubscription, 2_000);
}

function renderOverlay(s: CallUiState | null): void {
  const root = document.getElementById(OVERLAY_ID);
  if (!root) return;

  if (timerInterval) { clearInterval(timerInterval); timerInterval = null; }
  if (!s) {
    root.innerHTML = '';
    lastPhase = 'idle';
    keypadOpen = false;
    return;
  }

  if (s.phase !== lastPhase) keypadOpen = false;
  lastPhase = s.phase;

  if (s.phase === 'ringing-in') {
    root.innerHTML = renderIncoming(s);
    wireIncoming(s);
  } else if (s.phase === 'ended') {
    root.innerHTML = renderEnded(s);
    setTimeout(() => {
      const r = document.getElementById(OVERLAY_ID);
      if (r) r.innerHTML = '';
    }, 1200);
  } else if (s.minimised) {
    root.innerHTML = renderPill(s);
    wirePill();
    timerInterval = setInterval(updateTimer, 1000);
  } else {
    root.innerHTML = renderActive(s);
    wireActive(s);
    timerInterval = setInterval(updateTimer, 1000);
  }
}

// ── Incoming ─────────────────────────────────────────────────────────────

function renderIncoming(s: CallUiState): string {
  const cp = displayCounterparty(s);
  const sub = s.call.counterparty_phone;
  return `
    <div id="call-ring-overlay" class="call-ring-overlay" role="dialog" aria-modal="true">
      <div class="call-ring-info">
        <div class="call-ring-label">Incoming call</div>
        <div class="call-ring-name">${esc(cp)}</div>
        <div class="call-ring-phone">${esc(sub)}</div>
      </div>
      <div class="call-ring-avatar">
        <svg width="68" height="68" viewBox="0 0 24 24" fill="currentColor"><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1 1 0 0 0-1.02.24l-2.2 2.2a15.1 15.1 0 0 1-6.59-6.58l2.2-2.21a1 1 0 0 0 .25-1.02A11.4 11.4 0 0 1 8.5 4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1 17 17 0 0 0 17 17 1 1 0 0 0 1-1v-3.5a1 1 0 0 0-1-1z"/></svg>
      </div>
      <div class="call-ring-actions">
        <button id="call-ring-decline" class="call-ring-btn call-ring-btn-decline" aria-label="Decline">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" style="transform:rotate(135deg)"><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1 1 0 0 0-1.02.24l-2.2 2.2a15.1 15.1 0 0 1-6.59-6.58l2.2-2.21a1 1 0 0 0 .25-1.02A11.4 11.4 0 0 1 8.5 4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1 17 17 0 0 0 17 17 1 1 0 0 0 1-1v-3.5a1 1 0 0 0-1-1z"/></svg>
        </button>
        <button id="call-ring-accept" class="call-ring-btn call-ring-btn-accept" aria-label="Accept">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1 1 0 0 0-1.02.24l-2.2 2.2a15.1 15.1 0 0 1-6.59-6.58l2.2-2.21a1 1 0 0 0 .25-1.02A11.4 11.4 0 0 1 8.5 4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1 17 17 0 0 0 17 17 1 1 0 0 0 1-1v-3.5a1 1 0 0 0-1-1z"/></svg>
        </button>
      </div>
    </div>
  `;
}

let incomingKeyHandler: ((e: KeyboardEvent) => void) | null = null;

function wireIncoming(s: CallUiState): void {
  const ownerName = state.ownerId
    ? state.people.find(p => p.id === state.ownerId)?.name
    : undefined;
  const acceptFn = (): void => { void acceptIncoming(s.call.id, ownerName); };
  const declineFn = (): void => { void decline(s.call.id); };
  document.getElementById('call-ring-accept')?.addEventListener('click', acceptFn);
  document.getElementById('call-ring-decline')?.addEventListener('click', declineFn);
  if (incomingKeyHandler) document.removeEventListener('keydown', incomingKeyHandler);
  incomingKeyHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') { e.preventDefault(); acceptFn(); }
    else if (e.key === 'Escape') { e.preventDefault(); declineFn(); }
  };
  document.addEventListener('keydown', incomingKeyHandler);
}

// ── Active ───────────────────────────────────────────────────────────────

function renderActive(s: CallUiState): string {
  const cp = displayCounterparty(s);
  const sub = s.call.counterparty_phone;
  const label = s.phase === 'ringing-out' ? 'Calling…' : timerLabel(s);
  const connected = s.connected || s.phase === 'active';
  const timerClass = connected ? 'call-active-timer call-timer-connected' : 'call-active-timer';
  const muteActive = s.muted ? ' call-ctl-active' : '';
  const keypadActive = keypadOpen ? ' call-ctl-active' : '';
  // speakerOff = active (white bg means output is muted / headphone mode)
  const speakerActive = !s.speakerOn ? ' call-ctl-active' : '';
  return `
    <div id="call-active-card" class="call-active-backdrop" role="dialog" aria-modal="true">
      <div class="call-active-panel">
        <div class="call-active-header">
          <button id="call-minimise" class="call-active-header-minimise" title="Minimise" aria-label="Minimise">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h12v2H6z"/></svg>
          </button>
          <div class="call-active-header-label">
            ${s.call.direction === 'in' ? 'Incoming call' : 'Outgoing call'}
          </div>
          <div class="call-active-header-spacer"></div>
        </div>
        <div class="call-active-avatar">
          ${esc((cp || '?').charAt(0).toUpperCase())}
        </div>
        <div class="call-active-name">${esc(cp)}</div>
        <div class="call-active-phone">${esc(sub)}</div>
        <div id="call-timer" class="${timerClass}" data-answered="${s.answeredAt ?? ''}">${esc(label)}</div>
        ${s.dtmfDigits ? `<div class="call-active-phone" title="Digits sent" style="letter-spacing:.2em;opacity:.85">${esc(s.dtmfDigits)}</div>` : ''}

        ${keypadOpen ? renderKeypad() : ''}

        <div class="call-ctl-row">
          <button id="call-mute" class="call-ctl${muteActive}" aria-pressed="${s.muted}" title="${s.muted ? 'Unmute' : 'Mute'}">
            ${s.muted ? iconMicOff() : iconMicOn()}
          </button>
          <button id="call-keypad" class="call-ctl${keypadActive}" aria-pressed="${keypadOpen}" title="Keypad">
            ${iconKeypad()}
          </button>
          <button id="call-speaker" class="call-ctl${speakerActive}" aria-pressed="${s.speakerOn}" title="${s.speakerOn ? 'Mute output' : 'Unmute output'}">
            ${s.speakerOn ? iconSpeakerOn() : iconSpeakerOff()}
          </button>
        </div>

        <button id="call-end" class="call-end-btn" aria-label="End">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" style="transform:rotate(135deg)"><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1 1 0 0 0-1.02.24l-2.2 2.2a15.1 15.1 0 0 1-6.59-6.58l2.2-2.21a1 1 0 0 0 .25-1.02A11.4 11.4 0 0 1 8.5 4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1 17 17 0 0 0 17 17 1 1 0 0 0 1-1v-3.5a1 1 0 0 0-1-1z"/></svg>
        </button>
      </div>
    </div>
  `;
}

function renderKeypad(): string {
  const keys = ['1','2','3','4','5','6','7','8','9','*','0','#'];
  return `
    <div id="call-keypad-grid" class="call-keypad-grid">
      ${keys.map(k => `
        <button class="call-dtmf" data-digit="${esc(k)}">${esc(k)}</button>
      `).join('')}
    </div>
  `;
}

function wireActive(s: CallUiState): void {
  document.getElementById('call-minimise')?.addEventListener('click', toggleMinimise);
  document.getElementById('call-mute')?.addEventListener('click', () => { void toggleMute(); });
  document.getElementById('call-speaker')?.addEventListener('click', toggleSpeaker);
  document.getElementById('call-end')?.addEventListener('click', () => { void hangup(); });
  document.getElementById('call-keypad')?.addEventListener('click', () => {
    keypadOpen = !keypadOpen;
    renderOverlay(s);
  });
  document.querySelectorAll<HTMLButtonElement>('.call-dtmf').forEach(btn => {
    btn.addEventListener('click', () => {
      const d = btn.dataset.digit ?? '';
      if (d) void sendDtmf(d);
    });
  });
}

// ── Minimised pill ───────────────────────────────────────────────────────

function renderPill(s: CallUiState): string {
  const label = timerLabel(s);
  const cp = displayCounterparty(s);
  const dotClass = s.connected ? 'call-pill-dot call-pill-dot-connected' : 'call-pill-dot';
  return `
    <div id="call-pill" class="call-pill" role="button" tabindex="0">
      <span class="${dotClass}"></span>
      <span id="call-pill-timer" class="call-pill-timer" data-answered="${s.answeredAt ?? ''}">${esc(label)}</span>
      <span class="call-pill-name">${esc(cp)}</span>
      <button id="call-pill-end" class="call-pill-end" title="End" aria-label="End">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="transform:rotate(135deg)"><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1 1 0 0 0-1.02.24l-2.2 2.2a15.1 15.1 0 0 1-6.59-6.58l2.2-2.21a1 1 0 0 0 .25-1.02A11.4 11.4 0 0 1 8.5 4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1 17 17 0 0 0 17 17 1 1 0 0 0 1-1v-3.5a1 1 0 0 0-1-1z"/></svg>
      </button>
    </div>
  `;
}

function wirePill(): void {
  document.getElementById('call-pill')?.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('#call-pill-end')) return;
    toggleMinimise();
  });
  document.getElementById('call-pill-end')?.addEventListener('click', (e) => {
    e.stopPropagation();
    void hangup();
  });
}

// ── Ended flash ──────────────────────────────────────────────────────────

function renderEnded(s: CallUiState): string {
  const cp = displayCounterparty(s);
  const dur = s.answeredAt ? formatSeconds(Math.floor((Date.now() - s.answeredAt) / 1000)) : 'Not answered';
  return `
    <div class="call-ended-flash">
      <span>Call ended · ${esc(cp)}</span>
      <span class="call-ended-flash-dur">${esc(dur)}</span>
    </div>
  `;
}

// ── Timer ────────────────────────────────────────────────────────────────

function timerLabel(s: CallUiState): string {
  if (s.answeredAt) {
    const secs = Math.floor((Date.now() - s.answeredAt) / 1000);
    return formatSeconds(secs);
  }
  if (s.phase === 'ringing-out') return 'Calling…';
  if (s.phase === 'ringing-in') return 'Ringing…';
  return 'Connecting…';
}

function updateTimer(): void {
  const el = document.getElementById('call-timer') || document.getElementById('call-pill-timer');
  if (!el) return;
  const ans = el.getAttribute('data-answered');
  if (!ans) return;
  const answered = Number(ans);
  if (!answered) return;
  const secs = Math.floor((Date.now() - answered) / 1000);
  el.textContent = formatSeconds(secs);
}

function formatSeconds(secs: number): string {
  if (!Number.isFinite(secs) || secs < 0) secs = 0;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const pad = (n: number): string => n < 10 ? `0${n}` : String(n);
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function displayCounterparty(s: CallUiState): string {
  return s.call.counterparty_name && s.call.counterparty_name.trim()
    ? s.call.counterparty_name
    : s.call.counterparty_phone;
}

function esc(s: string | number): string {
  const el = document.createElement('div');
  el.textContent = String(s);
  return el.innerHTML;
}

// ── Control icons ────────────────────────────────────────────────────────

function iconMicOn(): string {
  return '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z"/></svg>';
}
function iconMicOff(): string {
  return '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zM4.27 3 3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/></svg>';
}
function iconSpeakerOn(): string {
  return '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>';
}
function iconSpeakerOff(): string {
  return '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03V9.9l2.48 2.48C16.5 12.25 16.5 12.12 16.5 12zM19 12c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.9 8.9 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.17v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>';
}
function iconKeypad(): string {
  return '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><circle cx="6" cy="6" r="1.6"/><circle cx="12" cy="6" r="1.6"/><circle cx="18" cy="6" r="1.6"/><circle cx="6" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="18" cy="12" r="1.6"/><circle cx="6" cy="18" r="1.6"/><circle cx="12" cy="18" r="1.6"/><circle cx="18" cy="18" r="1.6"/></svg>';
}
